import React, { useState, useEffect } from 'react';
import { AdminNavigationDrawer } from '../../components/admin/AdminNavigationDrawer';
import { useOTPAuth } from '../../hooks/useOTPAuth';
import {
  adminService,
  LlmProvider,
  LlmModel,
  CreateProviderRequest,
  UpdateProviderRequest,
  CreateModelRequest,
  UpdateModelRequest,
} from '@/services/adminService';

type TabType = 'providers' | 'models';

export const ManageLlmProvidersPage: React.FC = () => {
  const { user, logout } = useOTPAuth();
  const [activeTab, setActiveTab] = useState<TabType>('providers');
  const [providers, setProviders] = useState<LlmProvider[]>([]);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);

  // Provider dialog state
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LlmProvider | null>(null);

  // Model dialog state
  const [showModelDialog, setShowModelDialog] = useState(false);
  const [editingModel, setEditingModel] = useState<LlmModel | null>(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'providers') {
        const data = await adminService.getAllProviders();
        setProviders(data);
      } else {
        const data = await adminService.getAllModels();
        setModels(data);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch data';
      setError(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProvider = () => {
    setEditingProvider(null);
    setShowProviderDialog(true);
  };

  const handleEditProvider = (provider: LlmProvider) => {
    setEditingProvider(provider);
    setShowProviderDialog(true);
  };

  const handleDeleteProvider = async (provider: LlmProvider) => {
    if (
      !confirm(
        `Are you sure you want to delete provider "${provider.display_name}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await adminService.deleteProvider(provider.provider_id);
      // Invalidate provider cache so deletion takes effect immediately
      try {
        await adminService.invalidateProviderCache();
      } catch (cacheErr) {
        console.warn('Failed to invalidate provider cache:', cacheErr);
      }
      alert('Provider deleted successfully');
      fetchData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete provider';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleCreateModel = () => {
    setEditingModel(null);
    setShowModelDialog(true);
  };

  const handleEditModel = (model: LlmModel) => {
    setEditingModel(model);
    setShowModelDialog(true);
  };

  const handleDeleteModel = async (model: LlmModel) => {
    if (
      !confirm(
        `Are you sure you want to delete model "${model.model_name}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await adminService.deleteModel(model.id);
      alert('Model deleted successfully');
      fetchData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete model';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleSetDefaultModel = async (model: LlmModel) => {
    if (
      !confirm(
        `Set "${model.model_name}" as the default model?`
      )
    ) {
      return;
    }

    try {
      await adminService.updateModel(model.id, { is_default: true });
      alert(`"${model.model_name}" is now the default model`);
      fetchData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to set default model';
      alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="flex h-screen bg-[#F5F5F5]">
      <AdminNavigationDrawer
        user={user}
        onLogout={logout}
        isMobileOpen={isMobileDrawerOpen}
        onMobileClose={() => setIsMobileDrawerOpen(false)}
        onPinChange={setIsDrawerPinned}
      />

      <div className={`flex-1 flex flex-col ${isDrawerPinned ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="bg-[#F5F5F5] border-b border-gray-300 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileDrawerOpen(true)}
                className="md:hidden p-2 hover:bg-gray-200 rounded transition-colors"
                title="Open menu"
              >
                <svg className="w-6 h-6 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-2xl font-semibold text-[#080808] tracking-tight">
                Manage LLM Providers
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {activeTab === 'providers' ? `${providers.length} providers` : `${models.length} models`}
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="border-b border-gray-300 bg-white px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('providers')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'providers'
                  ? 'border-[#74AA9C] text-[#74AA9C]'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Providers
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'models'
                  ? 'border-[#74AA9C] text-[#74AA9C]'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Models
            </button>
          </div>
        </div>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Create Button */}
            <div className="mb-6">
              <button
                onClick={activeTab === 'providers' ? handleCreateProvider : handleCreateModel}
                className="px-6 py-3 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5C8A7E] transition-colors font-medium"
              >
                {activeTab === 'providers' ? 'Create Provider' : 'Create Model'}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-12 w-12" />
              </div>
            ) : activeTab === 'providers' ? (
              <ProvidersTable
                providers={providers}
                onEdit={handleEditProvider}
                onDelete={handleDeleteProvider}
              />
            ) : (
              <ModelsTable
                models={models}
                onEdit={handleEditModel}
                onDelete={handleDeleteModel}
                onSetDefault={handleSetDefaultModel}
              />
            )}
          </div>
        </main>
      </div>

      {/* Provider Dialog */}
      {showProviderDialog && (
        <ProviderDialog
          provider={editingProvider}
          onClose={() => setShowProviderDialog(false)}
          onSuccess={() => {
            setShowProviderDialog(false);
            fetchData();
          }}
        />
      )}

      {/* Model Dialog */}
      {showModelDialog && (
        <ModelDialog
          model={editingModel}
          providers={providers}
          onClose={() => setShowModelDialog(false)}
          onSuccess={() => {
            setShowModelDialog(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// ========== ProvidersTable Component ==========

interface ProvidersTableProps {
  providers: LlmProvider[];
  onEdit: (provider: LlmProvider) => void;
  onDelete: (provider: LlmProvider) => void;
}

const ProvidersTable: React.FC<ProvidersTableProps> = ({ providers, onEdit, onDelete }) => {
  if (providers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-[#4A4A4A]">No providers configured yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Endpoint
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {providers.map((provider) => (
              <tr
                key={provider.provider_id}
                onClick={() => onEdit(provider)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {provider.display_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                  {provider.provider_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {provider.provider_type}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">
                  {provider.api_endpoint || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      provider.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {provider.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(provider);
                    }}
                    className="text-[#74AA9C] hover:text-[#5C8A7E] mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(provider);
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== ModelsTable Component ==========

interface ModelsTableProps {
  models: LlmModel[];
  onEdit: (model: LlmModel) => void;
  onDelete: (model: LlmModel) => void;
  onSetDefault: (model: LlmModel) => void;
}

const ModelsTable: React.FC<ModelsTableProps> = ({ models, onEdit, onDelete, onSetDefault }) => {
  if (models.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-[#4A4A4A]">No models configured yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Model Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Context
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Input Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Output Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {models.map((model) => (
              <tr
                key={model.id}
                onClick={() => onEdit(model)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!model.is_default) {
                          onSetDefault(model);
                        }
                      }}
                      title={model.is_default ? 'Current default model' : 'Set as default model'}
                      className={`p-1 rounded transition-colors ${
                        model.is_default
                          ? 'text-yellow-500 cursor-default'
                          : 'text-gray-300 hover:text-yellow-500'
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill={model.is_default ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                        />
                      </svg>
                    </button>
                    {model.model_name}
                    {model.is_default && (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        Default
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {model.provider_display_name || model.provider_type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {model.context_window ? `${model.context_window.toLocaleString()}` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {model.input_price_per_1m_tokens ? `$${model.input_price_per_1m_tokens}/1M` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {model.output_price_per_1m_tokens ? `$${model.output_price_per_1m_tokens}/1M` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      model.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {model.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(model);
                    }}
                    className="text-[#74AA9C] hover:text-[#5C8A7E] mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(model);
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== ProviderDialog Component ==========

interface ProviderDialogProps {
  provider: LlmProvider | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ProviderDialog: React.FC<ProviderDialogProps> = ({ provider, onClose, onSuccess }) => {
  const isEdit = !!provider;
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateProviderRequest & { is_active?: boolean }>({
    provider_name: provider?.provider_name || '',
    display_name: provider?.display_name || '',
    provider_type: provider?.provider_type || 'anthropic',
    api_key: '',
    api_endpoint: provider?.api_endpoint || '',
    api_version: provider?.api_version || '',
    is_active: provider?.is_active ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isEdit) {
        // For edit, we don't send provider_name (it's immutable)
        const updateData: UpdateProviderRequest = {
          display_name: formData.display_name,
          provider_type: formData.provider_type,
          api_endpoint: formData.api_endpoint || undefined,
          api_version: formData.api_version || undefined,
          is_active: formData.is_active,
        };
        // Only include api_key if it's been changed
        if (formData.api_key) {
          updateData.api_key = formData.api_key;
        }
        await adminService.updateProvider(provider!.provider_id, updateData);
        // Invalidate provider cache so changes take effect immediately
        try {
          await adminService.invalidateProviderCache();
        } catch (cacheErr) {
          console.warn('Failed to invalidate provider cache:', cacheErr);
        }
        alert('Provider updated successfully');
      } else {
        await adminService.createProvider(formData);
        // Invalidate provider cache so new provider is available immediately
        try {
          await adminService.invalidateProviderCache();
        } catch (cacheErr) {
          console.warn('Failed to invalidate provider cache:', cacheErr);
        }
        alert('Provider created successfully');
      }
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save provider';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">
            {isEdit ? 'Edit Provider' : 'Create Provider'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider Name *
            </label>
            <input
              type="text"
              required
              disabled={isEdit}
              value={formData.provider_name}
              onChange={(e) => setFormData({ ...formData, provider_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C] disabled:bg-gray-100"
              placeholder="e.g., anthropic-prod"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">Provider name cannot be changed</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              required
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., Anthropic (Production)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider Type *
            </label>
            <select
              required
              value={formData.provider_type}
              onChange={(e) => setFormData({ ...formData, provider_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
            >
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
              <option value="google">Google</option>
              <option value="azure-ai-foundry">Azure AI Foundry</option>
              <option value="replicate">Replicate</option>
              <option value="perplexity">Perplexity</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key {isEdit && '(leave blank to keep current)'}
            </label>
            <input
              type="password"
              required={!isEdit}
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="Enter API key"
            />
            {isEdit && provider?.api_key_masked && (
              <p className="text-xs text-gray-500 mt-1 font-mono">
                Current: {provider.api_key_masked}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Endpoint (optional)
            </label>
            <input
              type="url"
              value={formData.api_endpoint}
              onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="https://api.example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Version (optional)
            </label>
            <input
              type="text"
              value={formData.api_version}
              onChange={(e) => setFormData({ ...formData, api_version: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., 2024-01-01"
            />
          </div>

          {isEdit && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-[#74AA9C] focus:ring-[#74AA9C] border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5C8A7E] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : isEdit ? 'Update Provider' : 'Create Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== ModelDialog Component ==========

interface ModelDialogProps {
  model: LlmModel | null;
  providers: LlmProvider[];
  onClose: () => void;
  onSuccess: () => void;
}

const ModelDialog: React.FC<ModelDialogProps> = ({ model, providers, onClose, onSuccess }) => {
  const isEdit = !!model;
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateModelRequest & { is_active?: boolean }>({
    model_name: model?.model_name || '',
    alias: model?.alias || '',
    release: model?.release || '',
    context_window: model?.context_window || undefined,
    input_price_per_1m_tokens: model?.input_price_per_1m_tokens || undefined,
    output_price_per_1m_tokens: model?.output_price_per_1m_tokens || undefined,
    provider_id: model?.provider_id || (providers.length > 0 ? providers[0].provider_id : 0),
    deployment_name: model?.deployment_name || '',
    is_default: model?.is_default || false,
    is_active: model?.is_active ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isEdit) {
        const updateData: UpdateModelRequest = {
          alias: formData.alias || undefined,
          release: formData.release,
          context_window: formData.context_window || undefined,
          input_price_per_1m_tokens: formData.input_price_per_1m_tokens || undefined,
          output_price_per_1m_tokens: formData.output_price_per_1m_tokens || undefined,
          provider_id: formData.provider_id,
          deployment_name: formData.deployment_name || undefined,
          is_active: formData.is_active,
          is_default: formData.is_default,
        };
        await adminService.updateModel(model!.id, updateData);
        alert('Model updated successfully');
      } else {
        await adminService.createModel(formData);
        alert('Model created successfully');
      }
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save model';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">
            {isEdit ? 'Edit Model' : 'Create Model'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model Name *
            </label>
            <input
              type="text"
              required
              disabled={isEdit}
              value={formData.model_name}
              onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C] disabled:bg-gray-100"
              placeholder="e.g., claude-sonnet-4-5-20250929"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">Model name cannot be changed</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider *
            </label>
            <select
              required
              value={formData.provider_id}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  provider_id: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
            >
              {providers.map((p) => (
                <option key={p.provider_id} value={p.provider_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Release *</label>
            <input
              type="text"
              required
              value={formData.release}
              onChange={(e) => setFormData({ ...formData, release: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., 4.5"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alias (optional)
            </label>
            <input
              type="text"
              value={formData.alias}
              onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., Claude Sonnet 4.5"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deployment Name (optional, for Azure)
            </label>
            <input
              type="text"
              value={formData.deployment_name}
              onChange={(e) => setFormData({ ...formData, deployment_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., gpt-4o-deployment"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Context Window (optional)
            </label>
            <input
              type="number"
              value={formData.context_window || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  context_window: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              placeholder="e.g., 200000"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Price per 1M tokens
              </label>
              <input
                type="number"
                step="0.000001"
                value={formData.input_price_per_1m_tokens || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    input_price_per_1m_tokens: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
                placeholder="e.g., 3.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Output Price per 1M tokens
              </label>
              <input
                type="number"
                step="0.000001"
                value={formData.output_price_per_1m_tokens || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    output_price_per_1m_tokens: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
                placeholder="e.g., 15.0"
              />
            </div>
          </div>

          <div className="space-y-2">
            {isEdit && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-[#74AA9C] focus:ring-[#74AA9C] border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="h-4 w-4 text-[#74AA9C] focus:ring-[#74AA9C] border-gray-300 rounded"
              />
              <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700">
                Set as default model
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5C8A7E] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : isEdit ? 'Update Model' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
